import dayjs from "dayjs";
import { isAttemptPending } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import type { PdpUrlAttempt, PdpUrlScore } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import { linkForPageVideo, ownLinkFor } from "../../../../../components/PdpPages/PdpJsonLdOutput";
import type { PageVideo, PdpUrlVideoLink } from "../../../../../components/PdpPages/PdpJsonLdOutput";
import { eligibilitySummary } from "../../../../../components/JsonLdEligibility/JsonLdEligibility";
import type { JsonLdValidation } from "../../../../../components/JsonLdEligibility/JsonLdEligibility";
import type { PdpChecks } from "../../../../../components/PdpPages/PdpChecks";
import type { PdpUrlScorePoint } from "../../../../../types/rmn";
import { apiErrorCode } from "../../../../../utils/apiError";
import { intervalLabel, scoringLimitMessage } from "../../../../../utils/plans";
import {
  TREND_MIN_POINTS,
  failedSinceScore,
  hasScore,
  humanReason,
  isStalled,
  minutesSince,
  scoredDate,
  urlHost,
  urlHostPath,
} from "../../../videos/selectedVideoCardV2/performance/pageScore";
import type { PdpUrlRow } from "../../../videos/selectedVideoCardV2/performance/pageScore";
import { movementSentence } from "../pageModel";
import type { MovementSentence } from "../pageModel";

/**
 * Product pages & audits on the product page — the rules, kept pure.
 *
 * Every derivation reads the live helpers (pageScore.ts, PdpScoreStatus)
 * rather than a second copy, so a page scores, stalls and fails the same way
 * here as on the video page and in the settings modal. The two modal helpers
 * the modal keeps private are copied verbatim, with their source lines.
 *
 * The role check arrives as a boolean, as cvModel's `internal` does: the caller
 * passes ScoreNowAction's `canScoreSync(role)`. That module imports useUser,
 * which builds the user store at import time, so a rule file doesn't import it.
 */

/** A listed page (`listProductPdpUrls`, no brandId — ProductPdpUrlsPanel.tsx:211). */
export interface PageRow extends PdpUrlRow {
  /** The row's own cadence, capped by the brand's plan, and the day it runs. */
  score_interval?: string | null;
  score_weekday?: number | null;
  score_day_of_month?: number | null;
}

/**
 * The whole list row the section draws — the modal's `PdpUrl`
 * (ProductPdpUrlsPanel.tsx:91-116) plus the `brand` the list includes
 * (desirelist-api pdpUrls.js:131-141). Rows come oldest first (`id ASC`).
 */
export interface PdpUrl extends PageRow {
  /** The owner brand: whose page this is. */
  brand?: { id: number; name?: string | null } | null;
  retailer?: string | null;
  /** On autopilot (switched per page in the modal's Autopilot tab). */
  autopilot?: boolean;
  /** Our videos on the page, one link each (TDC-1644). */
  videos?: PdpUrlVideoLink[] | null;
  product_json_ld?: any;
  product_json_ld_validation?: JsonLdValidation | null;
  /** What the last analysis found on the page — always a list on the list endpoint. */
  page_videos?: PageVideo[] | null;
  skus?: string[];
  checks?: PdpChecks | null;
}

// ---------------------------------------------------------------------------
// Page view
// ---------------------------------------------------------------------------

export type PageView =
  | "paused"
  | "scoring"
  | "queued"
  | "delayed"
  | "failed-newer"
  | "failed-never"
  | "scored"
  | "unscored";

/**
 * Which state one page block draws (the plan's Page view table; first match
 * wins).
 *
 * - paused: `active === false` — the last score stays on show.
 * - scoring: LOCAL, this block's sync Score now is out (`scoringPageId`).
 * - queued: a pending attempt inside the 15-minute bound (`isAttemptPending`).
 * - delayed: a pending attempt past it (`isStalled`, PENDING_STALE_MS) — the
 *   handoff's 20 minutes is not used.
 * - failed-newer: an error attempt newer than a real score (`failedSinceScore`).
 * - failed-never: an error attempt and no score.
 * - scored / unscored.
 */
export const pageView = (row: PdpUrlRow, scoringPageId?: number | null): PageView => {
  if (row.active === false) return "paused";
  if (scoringPageId != null && scoringPageId === row.id) return "scoring";
  if (isAttemptPending(row.latest_attempt)) return "queued";
  if (isStalled(row.latest_attempt)) return "delayed";
  if (failedSinceScore(row.latest_score, row.latest_attempt) && hasScore(row)) return "failed-newer";
  if (row.latest_attempt?.status === "error" && !hasScore(row)) return "failed-never";
  if (hasScore(row)) return "scored";
  return "unscored";
};

// ---------------------------------------------------------------------------
// Score block
// ---------------------------------------------------------------------------

type ScoreStage = "track" | "delta" | "trend";

/** 1 score = the track · 2–3 = the delta chip · 4+ (TREND_MIN_POINTS) = the trend. */
export const scoreStage = (count: number): ScoreStage => {
  if (count >= TREND_MIN_POINTS) return "trend";
  if (count >= 2) return "delta";
  return "track";
};

/**
 * A page's score series from `GET …/pdp-urls/:id/scores` (endpoints.ts:425).
 * The API answers OLDEST first (pdpUrls.js:551) while `movement`, `subDelta`
 * and `subPrevious` read [0] as now, so: drop runs without `scored_at` and
 * reverse — the video page's own adapter (pageScore.ts:148-159), copied.
 */
export const pageScoreHistory = (data: unknown): PdpUrlScore[] => {
  const points = (Array.isArray(data) ? data : (data as any)?.data || []) as PdpUrlScorePoint[];
  return points
    .filter((p) => p.scored_at)
    .map((p) => ({
      id: p.id,
      overall_score: p.overall_score,
      human_conversion_score: p.human_conversion_score,
      agentic_commerce_score: p.agentic_commerce_score,
      scored_at: p.scored_at,
    }))
    .reverse();
};

/**
 * "up from 70 on 10 Sep" / "same as 70 on 10 Sep": pages always date the
 * previous score to the day, across months too. `history` is newest first
 * (`pageScoreHistory`).
 */
export const pageMovement = (history: PdpUrlScore[] | null | undefined): MovementSentence | null =>
  movementSentence(history, (_now, prev) => (prev.scored_at ? `on ${dayjs(prev.scored_at).format("D MMM")}` : ""));

/**
 * The line under the score: "Scored 17 Sep 2026 · Weekly". The API returns no
 * next date, so the cadence stands in for it (DF-01); paused, or an older score
 * shown after a failure, prints the date only.
 */
export const provenanceLine = (row: PageRow, view: PageView): string => {
  const scored = `Scored ${scoredDate(row.latest_score?.scored_at)}`;
  if (view === "paused" || view === "failed-newer" || !row.score_interval) return scored;
  return `${scored} · ${intervalLabel(row.score_interval)}`;
};

// ---------------------------------------------------------------------------
// Score now / Run report
// ---------------------------------------------------------------------------

export const IN_PROGRESS_NOTE = "A score is already in progress.";
/** Beside a Score now another page's sync score holds — that page sits in another tab, out of sight. */
export const LOCKED_NOTE = "Another page is being scored.";

interface ScoreButtonState {
  /** sync = Score now (`canScoreSync`) · queued = Run report (everyone else). */
  kind: "sync" | "queued";
  label: string;
  /** Paused rows offer Resume scoring instead. */
  hidden: boolean;
  disabled: boolean;
  /** The line under the buttons, when the button explains itself. */
  note: string | null;
}

/**
 * The block's score button. `sync` = `canScoreSync(role)`
 * (VID/performance/ScoreNowAction.tsx:38), the API's `proTeamControl` list,
 * narrower than `isInternal`: a plain ACCOUNT_MANAGER takes Run report.
 *
 * Staff Score now is DISABLED while a report is queued, with "A score is
 * already in progress." beside it (the user's call, 2026-09-25 — the one
 * handoff state removed), and enabled again once the report is stalled, as
 * today (PdpScoreStatus.tsx:129), so staff can restart a stuck report.
 * `locked` = another block's sync score is out; the sync endpoint runs one
 * audit at a time. With the pages in tabs that page is out of sight, so the
 * held button says why (LOCKED_NOTE). Run report is held while queued or
 * delayed.
 */
export const scoreButton = (sync: boolean, view: PageView, locked = false): ScoreButtonState => {
  const hidden = view === "paused";
  const note = view === "queued" ? IN_PROGRESS_NOTE : null;
  if (sync) {
    return {
      kind: "sync",
      label: view === "scoring" ? "Scoring…" : "Score now",
      hidden,
      disabled: hidden || view === "scoring" || view === "queued" || locked,
      note: note || (locked && !hidden ? LOCKED_NOTE : null),
    };
  }
  return {
    kind: "queued",
    label: "Run report",
    hidden,
    disabled: hidden || view === "queued" || view === "delayed",
    note,
  };
};

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

export const HOURLY_LIMIT_MESSAGE = "You've reached the hourly limit for reports.";

/**
 * What a failed Score now, Run report or Resume leaves behind:
 * - `{ kind: "line" }` — the refusal line under the actions, in any view;
 * - `{ kind: "silent" }` — ScoreAlreadyInProgress: re-read the list, no line;
 * - `null` — not a refusal: the modal's toast, then the failure re-read.
 */
type Refusal = { kind: "line"; message: string } | { kind: "silent" };

export const refusalFor = (err: any): Refusal | null => {
  const code = apiErrorCode(err);
  if (code === "ScoreAlreadyInProgress") return { kind: "silent" };
  // 409 ScoringLimitReached and 409 ScoringInactive (pdpUrls.js:474-483):
  // `scoringLimitMessage` owns both strings (utils/plans.ts:74).
  if (code === "ScoringLimitReached" || code === "ScoringInactive") {
    const message = scoringLimitMessage(err);
    if (message) return { kind: "line", message };
  }
  if (err?.response?.status === 429) return { kind: "line", message: HOURLY_LIMIT_MESSAGE };
  return null;
};

// ---------------------------------------------------------------------------
// Section view
// ---------------------------------------------------------------------------

export type SectionView = "loading" | "error" | "none" | "card";

interface SectionState {
  /** The first list load is out. */
  loading: boolean;
  /** The list failed. Never drawn as "no pages" (the modal's `[]`, :215-219). */
  error: boolean;
}

/**
 * The section's shape: loading, the error block, the empty block (no pages,
 * the form closed), or the card that holds the pages ("card"): one page's
 * block alone, or, at 2+ pages, tabs with the chosen page's block
 * (`resolvePageTab`). An open form with no pages is the card too — the form
 * lives there. One page is the card as well: its block draws its own "Not
 * scored yet"; nothing collapses to a smaller strip (the video details page
 * has none).
 */
export const sectionView = (
  rows: ReadonlyArray<unknown> | null | undefined,
  formOpen: boolean,
  s: SectionState,
): SectionView => {
  if (s.loading) return "loading";
  if (s.error) return "error";
  if (!rows) return "loading";
  if (rows.length === 0 && !formOpen) return "none";
  return "card";
};

/** "Added, but paused": the create answered `active === false` for a page that wasn't listed before. */
export const isAddedPaused = (
  created: { id?: number | null; active?: boolean | null } | null | undefined,
  rowsBefore: Array<{ id: number }>,
): boolean => !!created && created.active === false && !rowsBefore.some((r) => r.id === created.id);

/**
 * The page an add created, so the section can open its tab: the create's own
 * id; else (a response without one) the page the fresh list has that the list
 * before the add didn't — the newest, should several have appeared (the list
 * is oldest first). Null when neither says.
 */
export const addedPageId = (
  created: { id?: number | null } | null | undefined,
  rowsBefore: Array<{ id: number }>,
  rowsAfter: Array<{ id: number }> | null | undefined,
): number | null => {
  if (created?.id != null) return created.id;
  const known = new Set(rowsBefore.map((r) => r.id));
  const fresh = (rowsAfter || []).filter((r) => !known.has(r.id));
  return fresh.length ? fresh[fresh.length - 1].id : null;
};

// ---------------------------------------------------------------------------
// Product-page tabs (2+ pages — the user's call, 2026-09-29; the handoff
// stacked the pages)
// ---------------------------------------------------------------------------

/**
 * The key that holds the selected product-page tab (the page's id), so a
 * refresh or a shared link opens the same page: `#pdpUrlId=151`. In the URL's
 * hash, not its query. The Videos list's writers build their URL from
 * window.location.search (hooks/useCommonParams.tsx:43-51) and its query key
 * is every param but a few (useVideosQuery.ts:20,34-39), so a query param
 * would be picked up by the router on the next video open and reload the grid
 * from page 1 on close, and be sent to the videos API (helper.ts
 * fetchVideoPage). Nothing in the portal reads the hash, and a router
 * navigation drops it, so it never carries to the list or the next product.
 */
export const PAGE_TAB_PARAM = "pdpUrlId";

/** The tab a URL's hash names: PAGE_TAB_PARAM as a positive whole number, else null. */
export const pageTabFromHash = (hash: string | null | undefined): number | null => {
  const raw = new URLSearchParams((hash || "").replace(/^#/, "")).get(PAGE_TAB_PARAM);
  if (!raw || !/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return id > 0 && Number.isSafeInteger(id) ? id : null;
};

/**
 * The same URL with PAGE_TAB_PARAM in its hash set to `id` (or removed, for
 * null) — the path, the query and anything else in the hash kept — or null
 * when it already says that, so the section writes nothing then.
 */
export const withPageTab = (
  loc: { pathname: string; search: string; hash: string },
  id: number | null,
): string | null => {
  const params = new URLSearchParams(loc.hash.replace(/^#/, ""));
  const want = id == null ? null : String(id);
  if (params.get(PAGE_TAB_PARAM) === want && params.getAll(PAGE_TAB_PARAM).length <= 1) return null;
  if (want == null) params.delete(PAGE_TAB_PARAM);
  else params.set(PAGE_TAB_PARAM, want);
  const hash = params.toString();
  return `${loc.pathname}${loc.search}${hash ? `#${hash}` : ""}`;
};

/**
 * The page a tab pick shows: `picked` while it is one of `ids`, else the
 * first (oldest). Null with no pages. A pick that isn't listed — a bad id in
 * the URL, a page deleted in Settings, an add whose re-read failed — shows
 * the first until (if ever) that page is listed.
 */
export const resolvePageTab = (ids: ReadonlyArray<number>, picked: number | null | undefined): number | null => {
  if (picked != null && ids.includes(picked)) return picked;
  return ids.length ? ids[0] : null;
};

/**
 * What PAGE_TAB_PARAM should say: the page on show, only while tabs show (2+
 * pages) and only when it isn't the first — no param already opens the
 * first (`resolvePageTab`), so the default URL stays as it was. Null = no param.
 */
export const pageTabParam = (ids: ReadonlyArray<number>, shownId: number | null): number | null =>
  ids.length >= 2 && shownId != null && shownId !== ids[0] ? shownId : null;

/** The tab after deleting `deletedId`: the page after it, else the one before; null if it was the only one (or isn't listed). */
export const pageAfterDelete = (ids: ReadonlyArray<number>, deletedId: number): number | null => {
  const at = ids.indexOf(deletedId);
  if (at < 0) return null;
  if (at + 1 < ids.length) return ids[at + 1];
  return at > 0 ? ids[at - 1] : null;
};

interface TabRow extends PdpUrlRow {
  brand?: { name?: string | null } | null;
}

interface PageTabItem {
  id: number;
  /** The site, told apart from a sibling on the same site (`pageTabLabels`). */
  label: string;
  /** The latest overall score, or null when the page has none (`hasScore`). */
  score: number | null;
  /** "google.com, score 72" / "bestbuy.com, not scored yet". */
  ariaLabel: string;
  /** The full URL: the label is the site, and a long one is cut. */
  title: string;
}

const pathTail = (path: string): string => {
  const last = path.split("/").filter(Boolean).pop() || "";
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

/** Every value set, and no two alike (case aside). */
const allDistinct = (values: string[]): boolean =>
  values.every(Boolean) && new Set(values.map((v) => v.toLowerCase())).size === values.length;

/**
 * Each tab's label, in row order: the page's site (`urlParts` host, "www."
 * dropped; the raw URL when it isn't one). Pages on the SAME site are told
 * apart, the whole group one way, the first that works:
 *   1. the owner brand — "amazon.com · Acme" — when every page there has one
 *      and no two share it;
 *   2. the last part of the path — "amazon.com · B0C1234";
 *   3. the whole path — "brand.com/us/index.html";
 *   4. a number, oldest first — "google.com (2)" (the URLs differ only in
 *      their query, which the label never shows).
 */
export const pageTabLabels = (rows: ReadonlyArray<TabRow>): string[] => {
  const sites = rows.map((row, i) => urlParts(row.pdp_url).host || (row.pdp_url || "").trim() || `Page ${i + 1}`);
  const labels = [...sites];
  const groups = new Map<string, number[]>();
  sites.forEach((site, i) => {
    const key = site.toLowerCase();
    const members = groups.get(key);
    if (members) members.push(i);
    else groups.set(key, [i]);
  });
  groups.forEach((members) => {
    if (members.length < 2) return;
    const brands = members.map((i) => (rows[i].brand?.name || "").trim());
    const paths = members.map((i) => urlParts(rows[i].pdp_url).path);
    const tails = paths.map(pathTail);
    let tell: (i: number, k: number) => string;
    if (allDistinct(brands)) tell = (i, k) => `${sites[i]} · ${brands[k]}`;
    else if (allDistinct(tails)) tell = (i, k) => `${sites[i]} · ${tails[k]}`;
    else if (allDistinct(paths.map((p) => p || "/"))) tell = (i, k) => `${sites[i]}${paths[k]}`;
    else tell = (i, k) => `${sites[i]} (${k + 1})`;
    members.forEach((i, k) => {
      labels[i] = tell(i, k);
    });
  });
  return labels;
};

/** One tab per page, in row order (oldest first): label, score, names. */
export const pageTabItems = (rows: ReadonlyArray<TabRow>): PageTabItem[] => {
  const labels = pageTabLabels(rows);
  return rows.map((row, i) => {
    const score = hasScore(row) ? (row.latest_score?.overall_score as number) : null;
    return {
      id: row.id,
      label: labels[i],
      score,
      ariaLabel: score != null ? `${labels[i]}, score ${score}` : `${labels[i]}, not scored yet`,
      title: row.pdp_url,
    };
  });
};

// ---------------------------------------------------------------------------
// Copied from the settings modal (productsV2/productModal/ProductPdpUrlsPanel.tsx)
// ---------------------------------------------------------------------------

// Verbatim, ProductPdpUrlsPanel.tsx:62-72.
// 400 { error: 'IntervalExceedsPlanMax', max } from the API when a cadence
// faster than the brand's plan cap is requested.
export const intervalErrorMessage = (e: any): string | null => {
    const code = apiErrorCode(e);
    if (code === "IntervalExceedsPlanMax") {
        const max = e?.response?.data?.max;
        return `That refresh is faster than the brand's plan allows${max ? ` (max: ${intervalLabel(max)})` : ""}.`;
    }
    if (code === "InvalidPdpScoreInterval") return "Pick a valid refresh cadence.";
    return null;
};

// Verbatim, ProductPdpUrlsPanel.tsx:74-84.
// A linked video the last PDP analysis did not see on the page: it looked
// (page_videos is a list, not null) and no page video was matched to this
// link. Null when the page was never analyzed or the video is on it.
export const notOnPageNote = (row: { page_videos?: PageVideo[] | null; videos?: PdpUrlVideoLink[] | null }, link: PdpUrlVideoLink): string | null => {
    if (!Array.isArray(row.page_videos)) return null;
    const onPage = row.page_videos.some((v) => linkForPageVideo(row, v)?.s3_file_id === link.s3_file_id);
    if (onPage) return null;
    const others = row.page_videos.length;
    if (others === 0) return "Not found on this page by the last PDP analysis, which saw no video there.";
    return `Not found on this page by the last PDP analysis, which saw ${others === 1 ? "1 other video" : `${others} other videos`} there.`;
};

// ---------------------------------------------------------------------------
// Display rules for the page block
// ---------------------------------------------------------------------------

/** The line under Score now / Run report (handoff README:150, :195). */
export const SYNC_HELPER = "Fetches the live page, then runs one audit. Takes 30–90 seconds.";
export const ASYNC_HELPER = "Runs in the background — usually a few minutes.";
export const scoreHelper = (sync: boolean): string => (sync ? SYNC_HELPER : ASYNC_HELPER);

/** The URL as the handoff prints it: host in --text, the path after it in --text-2. */
export const urlParts = (raw?: string | null): { host: string; path: string } => {
  const host = urlHost(raw);
  const full = urlHostPath(raw);
  return { host, path: host && full.startsWith(host) ? full.slice(host.length) : "" };
};

/**
 * The list re-read while an attempt is pending (useProductPages'
 * usePendingScorePoll), and the queued strip's word for it — one number, so
 * the copy can't drift from the poll.
 */
export const PENDING_POLL_MS = 5000;
export const POLL_NOTE = `Checking every ${PENDING_POLL_MS / 1000} seconds`;

/** The working strip's copy for a page in flight (handoff README:197-199). Null for every other view. */
export const workingCopy = (
  view: PageView,
  sync: boolean,
  scored: boolean,
): { title: string; body: string } | null => {
  if (view === "scoring") {
    return {
      title: "Scoring this page",
      body: "Fetching the live page, then running one audit. Takes 30–90 seconds.",
    };
  }
  if (view === "queued") {
    if (!sync) return { title: "Report queued", body: ASYNC_HELPER };
    return { title: scored ? "Score queued" : "First score queued", body: "Fetches the live page, then runs one audit." };
  }
  if (view === "delayed") {
    return {
      title: "Taking longer than expected",
      body: sync ? "Score now restarts it." : "It's still running. No need to run it again.",
    };
  }
  return null;
};

/** "0:42" — the sync score's elapsed counter, counted from the click. */
export const elapsedLabel = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/** "Queued 22 min ago" — the delayed strip's counter, from the attempt's enqueue time. */
export const queuedAgo = (attempt?: PdpUrlAttempt | null): string => `Queued ${minutesSince(attempt?.created_at)} min ago`;

interface FailureCopy {
  title: string;
  body: string;
  /** The raw token — staff only. */
  token: string | null;
  /** humanReason knew it: print it small. Unknown = an unbounded string, so the MonoError block. */
  known: boolean;
}

/**
 * The alert strip for a failed attempt (handoff README:201-202). Known tokens
 * read as words (`humanReason`, pageScore.ts:360-375); an unknown one never
 * reaches a brand user's sentence — they get "Scoring failed", staff also get
 * the token.
 *
 * A reason that is already a sentence ("Page fetching is out of credits. An
 * admin needs to top this up.") can't take " on 22 Sep." on its end: the date
 * goes first, in its own sentence — "Failed on 22 Sep. Page fetching is…".
 */
export const failureCopy = (view: PageView, attempt?: PdpUrlAttempt | null): FailureCopy | null => {
  if (view !== "failed-newer" && view !== "failed-never") return null;
  const reason = humanReason(attempt?.error);
  const date = attempt?.created_at ? dayjs(attempt.created_at).format("D MMM") : null;
  const what = reason.known ? reason.text : "Scoring failed";
  const tail = view === "failed-newer" ? " Showing the previous score." : "";
  let sentence: string;
  if (/[.!?]$/.test(what)) sentence = date ? `Failed on ${date}. ${what}` : what;
  else sentence = `${what}${date ? ` on ${date}` : ""}.`;
  return {
    title: view === "failed-newer" ? "Last scoring attempt failed" : "Couldn't score this page",
    body: `${sentence}${tail}`,
    token: attempt?.error || null,
    known: reason.known,
  };
};

/** The identity row's state chip: Queued / Delayed (--tint), Not current (--down). */
export const stateChip = (view: PageView): { tone: "tint" | "down"; label: string } | null => {
  if (view === "queued") return { tone: "tint", label: "Queued" };
  if (view === "delayed") return { tone: "tint", label: "Delayed" };
  if (view === "failed-newer") return { tone: "down", label: "Not current" };
  return null;
};

/**
 * The page's series as the score block reads it: `[0]` must be the row's
 * `latest_score`. A rescore patches the row before the history refetch
 * lands; rather than compare the new headline with the old series (a wrong
 * delta) or drop the movement until the refetch returns (the trend would
 * blink off and back), the new score is put in front of the older series —
 * exactly what the refetch will bring. Anything else that disagrees is null:
 * say nothing about movement.
 */
export const inSyncHistory = (
  history: PdpUrlScore[] | null | undefined,
  latest?: PdpUrlScore | null,
): PdpUrlScore[] | null => {
  if (!history || history.length === 0 || !latest || latest.overall_score == null) return null;
  const [head] = history;
  if (head.id === latest.id) return history;
  if (history.some((h) => h.id === latest.id)) return null;
  const newer =
    !!latest.scored_at && !!head.scored_at && new Date(latest.scored_at).getTime() > new Date(head.scored_at).getTime();
  return newer ? [latest, ...history] : null;
};

/** The Score history table's date: "17 Sep 2026, 4:01 AM". */
export const historyDate = (at?: string | null): string => (at ? dayjs(at).format("D MMM YYYY, h:mm A") : "—");

/** Trend x labels: "10 Sep" (the plan's `formatLabel`). */
export const dayMonth = (iso: string): string => dayjs(iso).format("D MMM");

/** The trend's screen-reader name, every value listed (handoff README:253). Points are oldest first. */
export const trendAriaLabel = (points: Array<{ date: string; value: number }>): string =>
  `Overall score by score, oldest first: ${points.map((p) => `${p.value} on ${dayMonth(p.date)}`).join(", ")}`;

// The shared ScoreTrend's x-axis geometry (VID/performance/ScoreTrend.tsx:28-33,
// :50-51, :61-63, :71-74), mirrored so the page can tell which of its labels
// would collide. ScoreTrend itself is not edited.
const TREND_LEFT = 60;
const TREND_RIGHT_PAD = 20;
const TREND_MIN_WIDTH = 160;
const TREND_NARROW_BELOW = 320;
/** The least clear space between two trend labels, px. */
const TREND_LABEL_GAP = 8;

/**
 * Which x-labels the page's trend draws, as point indexes. ScoreTrend picks
 * up to five (first, last and every ceil(span / 4)th, :72-73) — a rule sized
 * for its 3-letter "Sep" labels; the page's "14 Sep" ones collide with it,
 * the last two most often (the end label is anchored at its right edge and
 * bold). So of ScoreTrend's own picks, left to right, a middle label is kept
 * only when it clears the one kept before it and the last by TREND_LABEL_GAP.
 * The first and last always stay. `hostWidth` is ScoreTrend's host width, as
 * it measures it (clientWidth); `measure(text, latest)` is a label's width
 * in px (`latest` = the bold end label). The caller hands ScoreTrend a
 * `formatLabel` that returns "" for the rest (an empty <text> draws nothing).
 */
export const trendLabelIndexes = (
  labels: ReadonlyArray<string>,
  hostWidth: number,
  measure: (text: string, latest: boolean) => number,
): number[] => {
  if (labels.length < 2) return labels.map((_, i) => i);
  const width = Math.max(TREND_MIN_WIDTH, hostWidth);
  const right = Math.max(TREND_LEFT + 40, width - TREND_RIGHT_PAD);
  const span = labels.length - 1;
  const x = (i: number) => TREND_LEFT + ((right - TREND_LEFT) * i) / span;
  const narrow = width < TREND_NARROW_BELOW;
  const every = narrow ? span : Math.max(1, Math.ceil(span / 4));
  const drawn = (i: number) => i === 0 || i === span || (!narrow && i % every === 0);
  const extent = (i: number): [number, number] => {
    const w = measure(labels[i], i === span);
    if (i === 0) return [x(0), x(0) + w];
    if (i === span) return [x(span) - w, x(span)];
    return [x(i) - w / 2, x(i) + w / 2];
  };
  const lastStart = extent(span)[0];
  const kept = [0];
  let end = extent(0)[1];
  for (let i = 1; i < span; i += 1) {
    if (drawn(i)) {
      const [a, b] = extent(i);
      if (a - end >= TREND_LABEL_GAP && lastStart - b >= TREND_LABEL_GAP) {
        kept.push(i);
        end = b;
      }
    }
  }
  kept.push(span);
  return kept;
};

// Copied from PdpJsonLdOutput.tsx:121-135 (both private there).
const KIND_WHERE: Record<string, string> = {
  iframe: "a player",
  jsonld: "the retailer markup",
  video_tag: "a video tag",
  inline: "a page script",
  attribute: "the page markup",
};
/**
 * "1:00". The total is rounded BEFORE it is split: the copied formatter
 * floored the minutes and then rounded the seconds, so 59.77s printed "0:60"
 * (the same one-line fix is in PdpJsonLdOutput.tsx). Exported for its tests.
 */
export const fmtDuration = (seconds?: number | null) => {
  if (!seconds || seconds <= 0) return null;
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * "0:48 · found in a video tag" — ONLY when the last analysis matched a page
 * video to this link (`linkForPageVideo`, PdpJsonLdOutput.tsx:87). Null
 * otherwise: the handoff drew it on every row, but nothing backs it there.
 */
export const ourVideoMeta = (
  row: { videos?: PdpUrlVideoLink[] | null; page_videos?: PageVideo[] | null },
  link: PdpUrlVideoLink,
): string | null => {
  const found = (row.page_videos || []).find((v) => linkForPageVideo(row, v)?.s3_file_id === link.s3_file_id);
  if (!found) return null;
  const parts = [fmtDuration(found.duration), `found in ${KIND_WHERE[found.kind] || found.kind}`].filter(Boolean);
  return parts.join(" · ");
};

/**
 * The Markup row's line: `eligibilitySummary` of the link the block speaks
 * for (`ownLinkFor`, no video in context = the page's first video) —
 * the same line PdpJsonLdOutput heads its body with (:98, :188-191). No
 * video on the page = no line.
 */
export const markupLine = (row: {
  videos?: PdpUrlVideoLink[] | null;
}): { text: string; tone: "up" | "down" | "neutral" } | null => {
  const own = ownLinkFor(row);
  if (!own) return null;
  const v = own.json_ld_validation;
  let tone: "up" | "down" | "neutral" = "neutral";
  if (v) tone = v.eligible ? "up" : "down";
  return { text: eligibilitySummary(v), tone };
};

// ---------------------------------------------------------------------------
// Add a product page — the Page URL field
// ---------------------------------------------------------------------------

/** The modal's empty-URL message (MODAL/ProductPdpUrlsPanel.tsx:243), here a field error. */
export const PAGE_URL_REQUIRED = "Please enter a PDP URL before saving.";

/**
 * The Edit dialog's message for a URL that isn't http(s): the modal's
 * (MODAL/ProductPdpUrlsPanel.tsx:447), verbatim. EditPageDialog shows this constant.
 */
export const PAGE_URL_NOT_HTTP = "Enter a full URL starting with http:// or https://";

/**
 * The Add form's Page URL check, on the trimmed value: empty is
 * PAGE_URL_REQUIRED; anything that isn't http:// or https:// followed by a
 * host is PAGE_URL_NOT_HTTP. The Edit dialog's test (`/^https?:\/\//i`,
 * EditPageDialog.tsx:72) plus a host, so a bare "https://" is refused too.
 * Null = valid.
 */
export const addPageUrlError = (raw: string | null | undefined): string | null => {
  const text = typeof raw === "string" ? raw.trim() : "";
  if (!text) return PAGE_URL_REQUIRED;
  return /^https?:\/\/[^\s/?#]/i.test(text) ? null : PAGE_URL_NOT_HTTP;
};
